"use client";

import { usePathname } from "next/navigation";
import type { Locale } from "@/lib/i18n/config";
import { MobileStickyAd } from "./mobile-sticky-ad";

/**
 * Page family for ad telemetry (`affiliate_impression.extra.zone`), from the
 * pathname alone so it works identically in every zone that mounts the bar.
 * Coarse on purpose: it exists to slice "ads per pageview" by the families the
 * coverage tile tracks, not to mirror the route table.
 */
export function stickyAdZoneOf(pathname: string | null): string {
	const parts = (pathname ?? "").split("/").filter(Boolean);
	// Drop the locale segment (`/en/...`, `/id/...`).
	if (parts[0] === "en" || parts[0] === "id") parts.shift();
	const [first, second] = parts;
	if (!first) return "home";
	if (first === "jobs") return second ? "job" : "jobs";
	if (first === "remote-jobs") return "jobs";
	if (first === "blog") return "blog";
	if (first === "seeker") return "hunt";
	if (
		first === "applications" ||
		first === "saved" ||
		first === "resume" ||
		first === "settings" ||
		first === "onboarding"
	)
		return "dashboard";
	if (first === "reports") return "reports";
	if (first === "tools") return "tools";
	return "other";
}

/**
 * Keyed by pathname so a client-side navigation remounts the bar: a fresh
 * offer request per page and a clean request→terminal accounting lifecycle
 * (`useSlotRequestTerminal` closes the old one on unmount). Mounted once per
 * zone layout; a bare `<MobileStickyAd>` in a layout fetched ONE offer per
 * layout lifetime (`ranRef`), which is why the owner saw the ad "only the
 * first time" (2026-09-27).
 */
export function MobileStickyAdRoute({ locale }: { locale: Locale }) {
	const pathname = usePathname();
	return (
		<MobileStickyAd
			key={pathname}
			locale={locale}
			zone={stickyAdZoneOf(pathname)}
		/>
	);
}
